import { z } from "zod";

/** An email address, as an account knows it: without spaces around it, and in lowercase. */
export const Email = z.string().trim().toLowerCase().max(254).pipe(z.email());

/**
 * `POST /sign-in/code`: emails a sign-in code to an address, for the device that signs this request. The share server
 * always answers that it sent one, so nobody can find out which addresses have an account.
 */
export const SignInCodeRequest = z.object({
    email: Email,
});

export type SignInCodeRequest = z.infer<typeof SignInCodeRequest>;

/** How many digits a sign-in code has. */
export const SIGN_IN_CODE_DIGITS = 8;

/**
 * `POST /sign-in`: signs the device that signs this request in to the account of an email address, with the code that
 * was sent there. A code only works on the device that asked for it. The first time, this makes the account.
 */
export const SignIn = z.object({
    email: Email,
    /** The code from the email. Spaces and dashes in it don't matter, as people may type it like the email shows it. */
    code: z
        .string()
        .transform((code) => code.replace(/[\s-]/g, ""))
        .pipe(z.string().regex(new RegExp(`^\\d{${SIGN_IN_CODE_DIGITS}}$`), `A code has ${SIGN_IN_CODE_DIGITS} digits`)),
    /** The name others see. Without it, a new account is named after its email address, until someone gives one. */
    name: z.string().trim().min(1).max(100).optional(),
});

export type SignIn = z.infer<typeof SignIn>;
